import { CloseJobActionPayload } from './dtos/job-actions'
import { JobServicePromiseClient } from '@taxfyle/api-internal/internal/job_grpc_web_pb'
import { JobAdminServicePromiseClient } from '@taxfyle/api-internal/internal/job_admin_grpc_web_pb'
import { DraftAdminServicePromiseClient } from '@taxfyle/api-internal/internal/draft_admin_grpc_web_pb'
import { TicketServicePromiseClient } from '@taxfyle/api-internal/internal/ticket_grpc_web_pb'
import { GetLatestOpenTicketsForJobsRequest } from '@taxfyle/api-internal/internal/ticket_pb'
import { ConsultationServicePromiseClient } from '@taxfyle/api-internal/internal/consultation_grpc_web_pb'
import { TaxPreparationServicePromiseClient } from '@taxfyle/api-internal/internal/tax_preparation_grpc_web_pb'
import { BookkeepingServicePromiseClient } from '@taxfyle/api-internal/internal/bookkeeping_grpc_web_pb'
import { DocumentRequestServicePromiseClient } from '@taxfyle/api-internal/internal/document_request_grpc_web_pb'
import { JobTaxAssistantServicePromiseClient } from '@taxfyle/api-internal/internal/job_tax_assistant_grpc_web_pb'
import { GetJobTaxAssistantRequest } from '@taxfyle/api-internal/internal/job_tax_assistant_pb'
import {
  PlaceJobOnHoldRequest,
  ForfeitJobRequest,
  RenameJobRequest,
  ResumeJobRequest,
  CloseJobRequest,
  SearchJobsRequest,
  GetJobRequest,
  ClaimJobRequest,
  CopyJobResponse,
  CopyJobRequest,
  CompleteTaskRequest,
  CompleteTaskResponse,
  UncompleteTaskResponse,
  UncompleteTaskRequest,
  ListJobQuestionsRequest,
  MakeCorrectionsRequest,
  CompleteInfoGatheringRequest,
  CompleteInfoGatheringResponse,
  RequestJobChampionProviderRequest,
  ClaimConsultationJobRequest,
  AddTeamMemberAsSupportingClientRequest,
  RemoveSupportingClientRequest,
  BulkCopyJobRequest,
  BulkCopyJobResponse,
} from '@taxfyle/api-internal/internal/job_pb'
import { SUPPORTED_ENGINE_VERSION } from 'consts/engine-version'
import feathers from 'feathers-client'
import feathersSocket from 'feathers-socketio/client'
import { MemberDTO } from 'iam/dtos/member'
import { APIClient } from 'infra/APIClient'
import { createGrpcAuthenticator } from 'infra/grpc-authenticator'
import memoize from 'memoizee'
import { defer, from, interval, Observable } from 'rxjs'
import {
  distinctUntilChanged,
  filter,
  map,
  share,
  startWith,
  switchMap,
} from 'rxjs/operators'
import { io as IO } from 'socket.io-client'
import { APIConfig, CursorPaged, OffsetPaged } from 'types/definitions'
import { rethrowRequestError } from 'utils/error-util'
import { enableDevTools } from 'utils/grpc-util'
import { filterNonNil, whileActive } from 'utils/rx-util'
import {
  CreateJobPayload,
  FindJobsQuery,
  JobDTO,
  PatchActionDTO,
  ProvidePaymentInfoPayload,
  TaskDTO,
  UpdateDraftPayload,
} from './dtos/job'
import { FindJobEventsQuery, JobEventDTO } from './dtos/job-event'
import { FetchPreviousProvidersForCurrentUserPayload } from './dtos/provider'
import { QuestionsQueryDTO, QuestionsResponseDTO } from './dtos/questions'
import {
  CreateAmendmentRequest,
  AddChampionProviderRequest,
  AddSupportingProviderRequest,
  RemoveJobCollaboratorRequest,
  TransferJobToPoolRequest,
  TransferJobToClientRequest,
  TransferJobToProviderRequest,
  TransferJobToClientTeamRequest,
  ReopenJobRequest,
  CompleteJobRequest,
  CancelJobRequest,
  CloseJobAsTestRequest,
  RemoveClientTeamFromJobRequest,
  ManuallySplitJobPayoutRequest,
  ArchiveJobRequest,
  ApplyCouponToJobRequest,
  RemoveCouponFromJobRequest,
  AddInvoiceRequest,
  ListJobQuestionVariablesRequest,
  OverrideDeadlineRequest,
  ChargeRequest,
  RefundRequest,
  AddSupportingClientRequest,
  NotifyProvidersOfOpenJobRequest,
  BulkArchiveJobsRequest,
  BulkRestoreJobsRequest,
  BulkTransferJobsToPoolRequest,
  BulkTransferJobsToClientTeamRequest,
  BulkTransferJobsToClientRequest,
  BulkTransferJobsToProviderRequest,
} from '@taxfyle/api-internal/internal/job_admin_pb'
import { DraftServicePromiseClient } from '@taxfyle/api-internal/internal/draft_grpc_web_pb'
import {
  SubmitDraftJobRequest,
  GetJobSubmissionProgressRequest,
  RequestChampionProviderRequest,
  GetQuoteRequest,
  ApplyCouponRequest,
  RemoveCouponRequest,
  ReplaceDraftQuestionAnswersRequest,
  UpgradeLegendRequest,
  CreateDraftJobRequest,
  CreateDraftJobForTeamRequest,
  ArchiveDraftRequest,
  ListPreviousProvidersRequest,
} from '@taxfyle/api-internal/internal/draft_pb'
import { ScopeChangeRequestAPI } from 'admin/scope-change-requests/API'
import { InsightsServicePromiseClient } from '@taxfyle/api-internal/internal/insights_grpc_web_pb'
import {
  GetJobInsightsQueryRequest,
  GetLatestJobInsightsRequest,
  GenerateJobInsightsRequest,
} from '@taxfyle/api-internal/internal/insights_pb'
import { InfoGatheringServicePromiseClient } from '@taxfyle/api-internal/internal/info_gathering_grpc_web_pb'
import {
  GetChecklistRequest,
  ProvideSupportingItemDocumentRequest,
  ProvideSupportingItemNotesRequest,
  RemoveSupportingItemDocumentRequest,
} from '@taxfyle/api-internal/internal/info_gathering_pb'
import {
  ListBookedConsultationsRequest,
  BookConsultationRequest,
  UnbookConsultationRequest,
  DownloadConsultationCalendarRequest,
} from '@taxfyle/api-internal/internal/consultation_pb'
import { GetDrakeTaxReturnFileInputsRequest } from '@taxfyle/api-internal/internal/tax_preparation_pb'
import { JobEventsServicePromiseClient } from '@taxfyle/api-internal/internal/job_event_grpc_web_pb'
import { ListJobEventsRequest } from '@taxfyle/api-internal/internal/job_event_pb'
import { RealtimeEvent } from '@taxfyle/api-internal/internal/realtime_pb'
import { getFeatureToggleClient } from 'infra/featureToggles'
import {
  CompleteBookkeepingCycleRequest,
  ConfirmUnmanagedPlatformAccessRequest,
  CorrectBookkeepingCycleRequest,
  GetBookkeepingProgressionInfoRequest,
  StartNewBookkeepingCycleRequest,
  SubmitBookkeepingAssessmentRequest,
} from '@taxfyle/api-internal/internal/bookkeeping_pb'
import TypeCase = RealtimeEvent.TypeCase
import { SubmitDocumentRequestInput } from '@taxfyle/api-internal/internal/document_request_pb'

export interface WorkAPI {
  jobs: JobsAPI
  drafts: DraftsAPI
  draftsAdmin: DraftsAdminAPI
  jobsV3: JobsV3API
  jobsV3Admin: JobsV3AdminAPI
  adminJobActions: AdminJobActionsAPI
  jobEvents: JobEventsAPI
  jobEventsV3: JobEventsV3API
  tasks: TaskAPI
  tickets: TicketsAPI
  questions: QuestionAPI
  providers: ProviderAPI
  scopeChangeRequests: ScopeChangeRequestAPI
  insights: InsightsAPI
  infoGathering: InfoGatheringAPI
  consultations: ConsultationAPI
  taxPreparation: TaxPreparationAPI
  documentRequests: DocumentRequestAPI
  jobTaxAssistants: JobTaxAssistantAPI
}

export function createWorkAPI(
  workOpts: APIConfig,
  workDotnetOpts: APIConfig,
  workAdminOpts: APIConfig,
  events$: Observable<RealtimeEvent>,
): WorkAPI {
  return {
    jobs: new JobsAPI(workOpts),
    drafts: new DraftsAPI(workDotnetOpts),
    draftsAdmin: new DraftsAdminAPI(workDotnetOpts),
    jobsV3: new JobsV3API(workDotnetOpts, events$),
    jobsV3Admin: new JobsV3AdminAPI(workDotnetOpts),
    adminJobActions: new AdminJobActionsAPI(workAdminOpts),
    tasks: new TaskAPI(workOpts),
    tickets: new TicketsAPI(workDotnetOpts),
    jobEvents: new JobEventsAPI(workOpts),
    jobEventsV3: new JobEventsV3API(workDotnetOpts, events$),
    questions: new QuestionAPI(workOpts),
    providers: new ProviderAPI(workOpts),
    scopeChangeRequests: new ScopeChangeRequestAPI(workDotnetOpts),
    insights: new InsightsAPI(workDotnetOpts),
    infoGathering: new InfoGatheringAPI(workDotnetOpts),
    consultations: new ConsultationAPI(workDotnetOpts),
    taxPreparation: new TaxPreparationAPI(workDotnetOpts),
    documentRequests: new DocumentRequestAPI(workDotnetOpts),
    jobTaxAssistants: new JobTaxAssistantAPI(workDotnetOpts),
  }
}

export interface IntegrationAPI {
  sureprep: SureprepAPI
}

export function createIntegrationsAPI(
  workDotnetOpts: APIConfig,
): IntegrationAPI {
  return {
    sureprep: new SureprepAPI(workDotnetOpts),
  }
}

const legacySortingFields: any = {
  name: 'name.raw',
  customer_first_name: 'customer_first.raw',
  deadline: 'date_deadline',
  modified: 'date_modified',
  waiting_on: 'waiting_on.keyword',
  milestone: 'milestone',
  status: 'status',
  date_transmitted: 'date_transmitted',
  date_created: 'date_created',
  date_completed: 'date_closed',
}

const useDotnetEvents = () => {
  return getFeatureToggleClient().variation(
    'WorkPortal.UseV3RealtimeEvents',
    false,
  )
}

export class AdminJobActionsAPI {
  readonly workAPI: APIClient

  constructor(workAPIConfig: APIConfig) {
    this.workAPI = new APIClient({
      ...workAPIConfig,
      globalParams: {
        ...workAPIConfig.globalParams,
        supported_engine_version: SUPPORTED_ENGINE_VERSION,
      },
    })
  }

  async closeJob(payload: CloseJobActionPayload) {
    return this.workAPI.client
      .post('admin/job-actions', {
        ...payload,
        action: 'CLOSE',
      })
      .then(this.workAPI._data)
      .catch(rethrowRequestError)
  }

  /**
   * Notifies pros of the available job.
   */
  async notifyProsOfOpenJob(jobId: string) {
    return this.workAPI.client
      .post('admin/job-actions', {
        action: 'NOTIFY',
        job: jobId,
      })
      .then(this.workAPI._data)
      .catch(rethrowRequestError)
  }
}

export class JobsAPI {
  readonly workAPI: APIClient

  private readonly useWorkApiEvents$ = interval(10_000).pipe(
    map(() => useDotnetEvents()),
    startWith(useDotnetEvents()), // start with current value
    map((useDotnet) => !useDotnet),
    distinctUntilChanged(),
  )

  constructor(workAPIConfig: APIConfig) {
    this.workAPI = new APIClient({
      ...workAPIConfig,
      globalParams: {
        ...workAPIConfig.globalParams,
        supported_engine_version: SUPPORTED_ENGINE_VERSION,
      },
    })
  }

  events() {
    return this.workAPI.config.token$.pipe(
      switchMap((token) =>
        fromFeathersEventStream<JobDTO>(
          this.workAPI.client.defaults.baseURL!,
          token,
          this.workAPI.config.globalParams,
          'jobs',
        ),
      ),
      map((message): FeathersEvent<JobDTO> | null => {
        return message.data.status !== 'UNCLAIMED' ? message : null
      }),
      whileActive(this.useWorkApiEvents$),
      filterNonNil(),
    )
  }

  /**
   * Create a Draft Job.
   *
   * @param props
   */
  create(props: CreateJobPayload): Promise<JobDTO> {
    return this.workAPI.client
      .post(`/jobs`, props)
      .then(this._data)
      .catch(rethrowRequestError)
  }

  /**
   * Updates the Draft Job.
   *
   * @param id
   * @param props
   */
  updateDraft(id: string, props: UpdateDraftPayload): Promise<JobDTO> {
    return this.workAPI.client
      .patch(`/jobs/${id}`, props)
      .then(this._data)
      .catch(rethrowRequestError)
  }

  /**
   * Deletes a Job.
   *
   * @param id
   */
  delete(id: string): Promise<unknown> {
    return this.workAPI.client
      .delete(`/jobs/${id}`)
      .then(this._data)
      .catch(rethrowRequestError)
  }

  /**
   * Collects payment on a Job.
   * @param id
   */
  settleBalance(id: string): Promise<JobDTO> {
    return this.workAPI.client
      .post(`/jobs/${id}/settle-balance`)
      .then(this._data)
      .catch(rethrowRequestError)
  }

  /**
   * Returns jobs.
   * @param params
   */
  find(params: FindJobsQuery): Promise<CursorPaged<JobDTO>> {
    params.$sort = params.$sort || {}

    // Convert params.$sort into query string format
    const iterableSort = params.$sort as any
    const sorts: any = {}
    for (const field in iterableSort) {
      const key = `$sort[${legacySortingFields[field]}][order]`
      sorts[key] = iterableSort[field]
    }
    const limit = params.$limit !== undefined ? params.$limit : 100
    const skip = params.after ? Number.parseInt(params.after) : 0
    return this.workAPI.client
      .get('/jobs', {
        params: {
          workspace_id: params.workspace_id,
          from_transmitted_date: params.from_transmitted_date,
          to_transmitted_date: params.to_transmitted_date,
          from_closed_date: params.from_closed_date,
          to_closed_date: params.to_closed_date,
          all: params.all,
          team_id: params.team_id,
          $limit: limit,
          $skip: skip,
          searchText: params.searchText,
          type: params.type,
          status: params.status,
          waiting_on: params.waiting_on,
          ...sorts,
        },
      })
      .then(this._data)
      .then((result: OffsetPaged<JobDTO>) => {
        // Convert offset paging into cursor paging. The real API returns
        // skip based offsets, but we'll return a "cursor" here which really
        // just contains the "skip" in order to make the API seamless whether
        // we're calling the new/old API.
        const data = Array.isArray(result) ? result : result.data
        const cursor =
          'skip' in result &&
          typeof result.skip === 'number' &&
          'total' in result &&
          typeof result.total === 'number'
            ? // Use the skip value returned in the result
              result.skip >= result.total
              ? null
              : String(result.skip)
            : // Fake it till you make it I guess
              data.length === 0
              ? null
              : (limit + skip).toString()

        return {
          data,
          cursor,
          total: result.total,
        }
      })
      .catch(rethrowRequestError)
  }

  getCurrentJobs(query: Partial<FindJobsQuery>): Promise<OffsetPaged<JobDTO>> {
    return this.workAPI.client
      .get('/jobs', {
        params: {
          ...query,
          status: 'CLAIMED',
          $limit: 1000,
        },
      })
      .then(this._data)
      .catch(rethrowRequestError)
  }

  getCompletedJobs(
    query: Partial<FindJobsQuery>,
  ): Promise<OffsetPaged<JobDTO>> {
    return this.workAPI.client
      .get('/jobs', {
        params: {
          ...query,
          as: 'wortal',
          status: 'CLOSED',
          $limit: 1000,
        },
      })
      .then(this._data)
      .catch(rethrowRequestError)
  }

  patch(
    jobId: string,
    params: Partial<JobDTO | PatchActionDTO>,
  ): Promise<JobDTO> {
    params = params || {}
    return this.workAPI.client
      .patch(`/jobs/${jobId}`, params)
      .then(this._data)
      .catch(rethrowRequestError)
  }

  providePaymentInfo(
    id: string,
    payload: ProvidePaymentInfoPayload,
  ): Promise<void> {
    return this.workAPI.client
      .post(`/jobs/${id}/payment-info`, payload)
      .then(this._data)
      .catch(rethrowRequestError)
  }

  /**
   * Helper to extract data.
   */
  _data<T>(resp: { data: T }) {
    return resp.data
  }
}

export class TicketsAPI {
  private readonly ticketGrpcClient = createTicketGrpcClient(
    this.workDotnetConfig.baseURL,
  )

  private readonly grpcAuthenticator = createGrpcAuthenticator(
    this.workDotnetConfig,
  )

  constructor(private workDotnetConfig: APIConfig) {}

  async getLatestOpenTicketsForJobs(
    request: GetLatestOpenTicketsForJobsRequest,
  ) {
    return this.ticketGrpcClient.getLatestOpenTicketsForJobs(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }
}

export class DraftsAPI {
  private readonly draftGrpcClient = createDraftGrpcClient(
    this.workDotnetConfig.baseURL,
  )

  private readonly grpcAuthenticator = createGrpcAuthenticator(
    this.workDotnetConfig,
  )

  constructor(private workDotnetConfig: APIConfig) {}

  async getQuote(request: GetQuoteRequest) {
    return this.draftGrpcClient.getQuote(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  async submitDraftJob(request: SubmitDraftJobRequest) {
    return this.draftGrpcClient.submitDraftJob(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  async requestChampionProvider(request: RequestChampionProviderRequest) {
    return this.draftGrpcClient.requestChampionProvider(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  async getJobSubmissionProgress(request: GetJobSubmissionProgressRequest) {
    return this.draftGrpcClient.getJobSubmissionProgress(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  async applyCoupon(request: ApplyCouponRequest) {
    return this.draftGrpcClient.applyCoupon(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  async removeCoupon(request: RemoveCouponRequest) {
    return this.draftGrpcClient.removeCoupon(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  async replaceQuestionAnswers(request: ReplaceDraftQuestionAnswersRequest) {
    return this.draftGrpcClient.replaceDraftQuestionAnswers(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  async upgradeLegend(request: UpgradeLegendRequest) {
    return this.draftGrpcClient.upgradeLegend(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  async createDraftJob(request: CreateDraftJobRequest) {
    return this.draftGrpcClient.createDraftJob(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  async createDraftJobForTeam(request: CreateDraftJobForTeamRequest) {
    return this.draftGrpcClient.createDraftJobForTeam(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  async deleteDraftJob(request: ArchiveDraftRequest) {
    return this.draftGrpcClient.archive(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  async listPreviousProviders(request: ListPreviousProvidersRequest) {
    return this.draftGrpcClient.listPreviousProviders(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }
}

export class JobsV3API {
  private readonly jobGrpcClient = createJobGrpcClient(
    this.workDotnetConfig.baseURL,
  )

  private readonly grpcAuthenticator = createGrpcAuthenticator(
    this.workDotnetConfig,
  )

  private readonly useDotnetApiEvents$ = interval(10_000).pipe(
    map(() => useDotnetEvents()),
    startWith(useDotnetEvents()), // start with current value
    distinctUntilChanged(),
  )

  /**
   * Stream of Job-related events.
   */
  readonly events$: Observable<RealtimeEvent>

  constructor(
    private workDotnetConfig: APIConfig,
    events$: Observable<RealtimeEvent>,
  ) {
    // Job events.
    this.events$ = events$.pipe(
      filter((event) => {
        // filter out non-op events.
        switch (event.getTypeCase()) {
          case RealtimeEvent.TypeCase.JOB_CREATED:
          case RealtimeEvent.TypeCase.JOB_UPDATED:
            return true
        }

        return false
      }),
      whileActive(this.useDotnetApiEvents$),
      share(),
    )
  }

  /**
   * Renames a job.
   */
  async renameJob(request: RenameJobRequest) {
    return this.jobGrpcClient.renameJob(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  /**
   * Places a job on hold.
   */
  async placeJobOnHold(request: PlaceJobOnHoldRequest) {
    return this.jobGrpcClient.placeJobOnHold(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  /**
   * Forfeit a job.
   */
  async forfeitJob(request: ForfeitJobRequest) {
    return this.jobGrpcClient.forfeitJob(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  /**
   * Resume a job that is on hold.
   */
  async resumeJob(request: ResumeJobRequest) {
    return this.jobGrpcClient.resumeJob(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  /**
   * Closes a job as provider.
   */
  async closeJobAsPro(request: CloseJobRequest) {
    return this.jobGrpcClient.closeJob(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  async searchJobs(request: SearchJobsRequest) {
    return this.jobGrpcClient.searchJobs(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  /**
   * Gets a job.
   */
  async getJob(request: GetJobRequest) {
    return this.jobGrpcClient.getJob(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  /**
   * Claim a job.
   */
  async claimJob(request: ClaimJobRequest) {
    return this.jobGrpcClient.claimJob(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  /**
   * Copy a job
   */
  async copyJob(request: CopyJobRequest): Promise<CopyJobResponse> {
    return this.jobGrpcClient.copyJob(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  async bulkCopyJobs(
    request: BulkCopyJobRequest,
  ): Promise<BulkCopyJobResponse> {
    return this.jobGrpcClient.bulkCopyJob(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  async completeTask(
    request: CompleteTaskRequest,
  ): Promise<CompleteTaskResponse> {
    return this.jobGrpcClient.completeTask(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  async uncompleteTask(
    request: UncompleteTaskRequest,
  ): Promise<UncompleteTaskResponse> {
    return this.jobGrpcClient.uncompleteTask(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  async listQuestions(request: ListJobQuestionsRequest) {
    return this.jobGrpcClient.listJobQuestions(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  async makeCorrections(request: MakeCorrectionsRequest) {
    return this.jobGrpcClient.makeCorrections(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  async claimConsultationJob(request: ClaimConsultationJobRequest) {
    return this.jobGrpcClient.claimConsultationJob(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  /**
   * Completes info gathering.
   * @param request
   */
  async completeInfoGathering(
    request: CompleteInfoGatheringRequest,
  ): Promise<CompleteInfoGatheringResponse> {
    return this.jobGrpcClient.completeInfoGathering(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  async addTeamMemberAsSupportingClient(
    request: AddTeamMemberAsSupportingClientRequest,
  ) {
    return this.jobGrpcClient.addTeamMemberAsSupportingClient(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  async removeSupportingClient(request: RemoveSupportingClientRequest) {
    return this.jobGrpcClient.removeSupportingClient(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }
}

export class DraftsAdminAPI {
  private readonly adminDraftGrpcClient = createDraftAdminGrpcClient(
    this.workDotnetConfig.baseURL,
  )

  private readonly grpcAuthenticator = createGrpcAuthenticator(
    this.workDotnetConfig,
  )

  constructor(private workDotnetConfig: APIConfig) {}

  async addInvoice(request: AddInvoiceRequest) {
    return this.adminDraftGrpcClient.addInvoice(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }
}

export class JobsV3AdminAPI {
  private readonly adminJobGrpcClient = createAdminJobGrpcClient(
    this.workDotnetConfig.baseURL,
  )

  private readonly grpcAuthenticator = createGrpcAuthenticator(
    this.workDotnetConfig,
  )

  constructor(private workDotnetConfig: APIConfig) {}

  /**
   * Creates an amendment to the job.
   */
  async createAmendment(request: CreateAmendmentRequest) {
    return this.adminJobGrpcClient.createAmendment(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  /**
   * Transfer job to pool.
   */
  async transferJobToPool(request: TransferJobToPoolRequest) {
    return this.adminJobGrpcClient.transferJobToPool(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  async bulkTransferJobToPool(request: BulkTransferJobsToPoolRequest) {
    return this.adminJobGrpcClient.bulkTransferJobsToPool(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  /**
   * Transfer job to client.
   */
  async transferJobToClient(request: TransferJobToClientRequest) {
    return this.adminJobGrpcClient.transferJobToClient(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  /**
   * Transfer job to pro.
   */
  async transferJobToPro(request: TransferJobToProviderRequest) {
    return this.adminJobGrpcClient.transferJobToProvider(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  /**
   * Transfer job to client team.
   */
  async transferJobToClientTeam(request: TransferJobToClientTeamRequest) {
    return this.adminJobGrpcClient.transferJobToClientTeam(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  async bulkTransferJobToClientTeam(
    request: BulkTransferJobsToClientTeamRequest,
  ) {
    return this.adminJobGrpcClient.bulkTransferJobsToClientTeam(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  async bulkTransferJobsToClient(request: BulkTransferJobsToClientRequest) {
    return this.adminJobGrpcClient.bulkTransferJobsToClient(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  async bulkTransferJobsToProvider(request: BulkTransferJobsToProviderRequest) {
    return this.adminJobGrpcClient.bulkTransferJobsToProvider(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  /**
   * Removes collaborator as Admin.
   */
  async removeJobCollaborator(request: RemoveJobCollaboratorRequest) {
    return this.adminJobGrpcClient.removeJobCollaborator(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  /**
   * Requests a champion Provider as Admin.
   */
  async requestJobChampionProvider(request: RequestJobChampionProviderRequest) {
    return this.adminJobGrpcClient.requestChampionProvider(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  /**
   * Adds a champion Provider as Admin.
   */
  async addJobChampionProvider(request: AddChampionProviderRequest) {
    return this.adminJobGrpcClient.addChampionProvider(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  /**
   * Adds a supporting Provider as Admin.
   */
  async addJobSupportingProvider(request: AddSupportingProviderRequest) {
    return this.adminJobGrpcClient.addSupportingProvider(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  /**
   * Adds a supporting Client as Admin.
   */
  async addJobSupportingClient(request: AddSupportingClientRequest) {
    return this.adminJobGrpcClient.addSupportingClient(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  /**
   * Reopen job as an Admin.
   */
  async reopenJob(request: ReopenJobRequest) {
    return this.adminJobGrpcClient.reopenJob(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  /**
   * Remove client team from a job as Admin.
   */
  async removeClientTeamFromJob(request: RemoveClientTeamFromJobRequest) {
    return this.adminJobGrpcClient.removeClientTeamFromJob(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  /**
   * Completes a job as Admin.
   */
  async completeJob(request: CompleteJobRequest) {
    return this.adminJobGrpcClient.completeJob(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  /**
   * Cancels a job as Admin.
   */
  async cancelJob(request: CancelJobRequest) {
    return this.adminJobGrpcClient.cancelJob(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  /**
   * Closes job as Test as Admin.
   */
  async closeJobAsTest(request: CloseJobAsTestRequest) {
    return this.adminJobGrpcClient.closeJobAsTest(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  /**
   * Manually splits the job payout.
   */
  async manuallySplitJobPayout(request: ManuallySplitJobPayoutRequest) {
    return this.adminJobGrpcClient.manuallySplitJobPayout(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  async archiveJob(request: ArchiveJobRequest) {
    return this.adminJobGrpcClient.archiveJob(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  async addInvoice(request: AddInvoiceRequest) {
    return this.adminJobGrpcClient.addInvoice(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  async chargeJob(request: ChargeRequest) {
    return this.adminJobGrpcClient.chargeJob(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  async refundJob(request: RefundRequest) {
    return this.adminJobGrpcClient.refundJob(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  async overrideDeadline(request: OverrideDeadlineRequest) {
    return this.adminJobGrpcClient.overrideDeadline(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  async applyCoupon(request: ApplyCouponToJobRequest) {
    return this.adminJobGrpcClient.applyCoupon(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  async removeCoupon(request: RemoveCouponFromJobRequest) {
    return this.adminJobGrpcClient.removeCoupon(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  async listJobQuestionVariables(request: ListJobQuestionVariablesRequest) {
    return this.adminJobGrpcClient.listJobQuestionVariables(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  async notifyProsOfOpenJob(request: NotifyProvidersOfOpenJobRequest) {
    return this.adminJobGrpcClient.notifyProvidersOfOpenJob(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  async bulkArchiveJobs(request: BulkArchiveJobsRequest) {
    return this.adminJobGrpcClient.bulkArchive(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  async bulkRestoreJobs(request: BulkRestoreJobsRequest) {
    return this.adminJobGrpcClient.bulkRestore(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }
}

export class JobEventsAPI extends APIClient {
  private readonly useWorkApiEvents$ = interval(10_000).pipe(
    map(() => useDotnetEvents()),
    startWith(useDotnetEvents()), // start with current value
    map((useDotnet) => !useDotnet),
    distinctUntilChanged(),
  )

  events() {
    return this.config.token$.pipe(
      switchMap((token) =>
        fromFeathersEventStream<JobEventDTO>(
          this.client.defaults.baseURL!,
          token,
          this.config.globalParams,
          'job-events',
        ),
      ),
      whileActive(this.useWorkApiEvents$),
    )
  }

  find(query: FindJobEventsQuery): Promise<JobEventDTO[]> {
    return this.client
      .get('/job-events', { params: query })
      .then(this._data)
      .catch(rethrowRequestError)
  }
}

export class JobEventsV3API {
  private readonly jobEventsGrpcClient = createJobEventGrpcClient(
    this.workDotnetConfig.baseURL,
  )

  private readonly grpcAuthenticator = createGrpcAuthenticator(
    this.workDotnetConfig,
  )

  private readonly useDotnetApiEvents$ = interval(10_000).pipe(
    map(() => useDotnetEvents()),
    startWith(useDotnetEvents()),
    distinctUntilChanged(),
  )

  readonly events$: Observable<RealtimeEvent>

  constructor(
    private workDotnetConfig: APIConfig,
    events$: Observable<RealtimeEvent>,
  ) {
    // JobEvent realitme events.
    this.events$ = events$.pipe(
      filter(
        (event) =>
          event.getTypeCase() === RealtimeEvent.TypeCase.JOB_EVENT_RECORDED,
      ),
      whileActive(this.useDotnetApiEvents$),
      share(),
    )
  }

  /**
   * Lists Job Events.
   */
  async list(query: ListJobEventsRequest) {
    return this.jobEventsGrpcClient.listJobEvents(
      query,
      await this.grpcAuthenticator.getMeta(),
    )
  }
}

export class TaskAPI extends APIClient {
  /**
   * Completes a task, returns the updated job.
   *
   * @param {*} jobId
   * @param {*} taskId
   */
  complete(jobId: string, taskId: string) {
    return this.patch(jobId, taskId, {
      completed: true,
    }).catch(rethrowRequestError)
  }

  /**
   * Uncompletes a task, returns the updated job.
   *
   * @param {*} jobId
   * @param {*} taskId
   */
  uncomplete(jobId: string, taskId: string) {
    return this.patch(jobId, taskId, {
      completed: false,
    }).catch(rethrowRequestError)
  }

  /**
   * Patches a task.
   *
   * @param {*} jobId
   * @param {*} taskId
   * @param {*} data
   */
  patch(jobId: string, taskId: string, data: Pick<TaskDTO, 'completed'>) {
    return this.client
      .patch(`/jobs/${jobId}/tasks/${taskId}`, data)
      .then(this._data)
      .catch(rethrowRequestError)
  }
}

/**
 * API client for Sureprep integration.
 */
export class SureprepAPI extends APIClient {
  /**
   * Downloads PBFX File for the specified job.
   *
   * @param jobId
   */
  async downloadPBFXFile(jobId: string): Promise<void> {
    const data = await this.client
      .get(`/api/v1.0/sureprepintegration/download-pbfx?jobId=${jobId}`)
      .then(this._data)
      .catch(rethrowRequestError)

    const file = new Blob([data], { type: 'application/octet-stream' })
    const fileURL = URL.createObjectURL(file)
    const link = document.createElement('a')
    link.style.display = 'none'
    link.href = fileURL
    link.download = `binder-${jobId}.pbfx`
    link.click()
    window.URL.revokeObjectURL(link.href)
    link.remove()
  }
}

/**
 * API client for getting the question progression.
 */
export class QuestionAPI extends APIClient {
  /**
   * Gets the question progression for a particular job.
   *
   * @param payload
   */
  getQuestions(payload: QuestionsQueryDTO): Promise<QuestionsResponseDTO> {
    return this.client
      .post('/engine/questions', payload)
      .then(this._data)
      .catch(rethrowRequestError)
  }
}

/**
 * API client for fetching providers.
 */
export class ProviderAPI extends APIClient {
  /**
   * Fetches previous providers for the current user.
   *
   * @param payload
   */
  fetchPreviousProvidersForCurrentUser(
    payload: FetchPreviousProvidersForCurrentUserPayload,
  ): Promise<MemberDTO[]> {
    return this.client
      .get(`/job-providers`, {
        params: { workspace_id: payload.workspace_id },
      })
      .then(this._data)
      .catch(rethrowRequestError)
  }
}

/**
 * API client for Insights.
 */
export class InsightsAPI {
  private readonly insightsGrpcClient = createInsightsGrpcClient(
    this.workDotnetConfig.baseURL,
  )

  private readonly grpcAuthenticator = createGrpcAuthenticator(
    this.workDotnetConfig,
  )

  constructor(private workDotnetConfig: APIConfig) {}

  /**
   * Gets basic Job Insights.
   * @param request
   * @returns
   */
  async getJobInsightsQuery(request: GetJobInsightsQueryRequest) {
    return this.insightsGrpcClient.getJobInsightsQuery(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  /**
   * Gets the latest Job Insights.
   * @param request
   * @returns
   */
  async getLatestJobInsights(request: GetLatestJobInsightsRequest) {
    return this.insightsGrpcClient.getLatestJobInsights(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  /**
   * Generate Job Insights.
   * @param request
   * @returns
   */
  async generateJobInsights(request: GenerateJobInsightsRequest) {
    return this.insightsGrpcClient.generateJobInsights(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }
}

/**
 * API client for Info Gathering.
 */
export class InfoGatheringAPI {
  private readonly infoGatheringGrpcClient = createInfoGatheringGrpcClient(
    this.workDotnetConfig.baseURL,
  )

  private readonly grpcAuthenticator = createGrpcAuthenticator(
    this.workDotnetConfig,
  )

  constructor(private workDotnetConfig: APIConfig) {}

  /**
   * Gets the checklist for a job.
   * @param request
   * @returns
   */
  async getChecklist(request: GetChecklistRequest) {
    return this.infoGatheringGrpcClient.getChecklist(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  /**
   * Request for providing a document supporting item.
   * @param request
   * @returns
   */
  async provideSupportingItemDocument(
    request: ProvideSupportingItemDocumentRequest,
  ) {
    return this.infoGatheringGrpcClient.provideSupportingItemDocument(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  /**
   * Request for removing a document from a supporting item.
   * @param request
   * @returns
   */
  async removeSupportingItemDocument(
    request: RemoveSupportingItemDocumentRequest,
  ) {
    return this.infoGatheringGrpcClient.removeSupportingItemDocument(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  /**
   * Request for providing a note supporting item.
   * @param request
   * @returns
   */
  async provideSupportingItemNotes(request: ProvideSupportingItemNotesRequest) {
    return this.infoGatheringGrpcClient.provideSupportingItemNotes(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }
}

/**
 * API client for Consultations.
 */
export class ConsultationAPI {
  private readonly consultationGrpcClient = createConsultationGrpcClient(
    this.workDotnetConfig.baseURL,
  )

  private readonly grpcAuthenticator = createGrpcAuthenticator(
    this.workDotnetConfig,
  )

  constructor(private workDotnetConfig: APIConfig) {}

  /**
   * Lists all booked consultations for a user.
   */
  async listBookedConsultations(request: ListBookedConsultationsRequest) {
    return this.consultationGrpcClient.listBookedConsultations(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  /**
   * Books a consultation.
   */
  async bookConsultation(request: BookConsultationRequest) {
    return this.consultationGrpcClient.bookConsultation(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  /**
   * Unbooks a consultation.
   */
  async unbookConsultation(request: UnbookConsultationRequest) {
    return this.consultationGrpcClient.unbookConsultation(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  /**
   * Downloads the consultation calendar.
   */
  async downloadConsultationCalendar(
    request: DownloadConsultationCalendarRequest,
  ) {
    return this.consultationGrpcClient.downloadConsultationCalendar(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }
}

/**
 * API client for TaxPreparation.
 */
export class TaxPreparationAPI {
  private readonly taxPreparationGrpcClient = createTaxPreparationGrpcClient(
    this.workDotnetConfig.baseURL,
  )

  private readonly grpcAuthenticator = createGrpcAuthenticator(
    this.workDotnetConfig,
  )

  constructor(private workDotnetConfig: APIConfig) {}

  /**
   * Gets all drake tax return file inputs needed for importing.
   */
  async getDrakeTaxReturnFileInputs(
    request: GetDrakeTaxReturnFileInputsRequest,
  ) {
    return this.taxPreparationGrpcClient.getDrakeTaxReturnFileInputs(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }
}

/**
 * API client for Bookkeeping
 */
export class BookkeepingAPI {
  private readonly bookkeepingGrpcClient = createBookkeepingGrpcClient(
    this.workDotnetConfig.baseURL,
  )

  private readonly grpcAuthenticator = createGrpcAuthenticator(
    this.workDotnetConfig,
  )

  /**
   * Stream of events for job progression.
   */
  readonly events$: Observable<RealtimeEvent>

  constructor(
    private workDotnetConfig: APIConfig,
    events$: Observable<RealtimeEvent>,
  ) {
    this.events$ = events$.pipe(
      filter((event) => event.getTypeCase() === TypeCase.BOOKKEEPING_UPDATED),
    )
  }

  /**
   * Gets the bookkeeping progression info.
   * @param request
   * @returns
   */
  async getBookkeepingProgressionInfo(
    request: GetBookkeepingProgressionInfoRequest,
  ) {
    return this.bookkeepingGrpcClient.getBookkeepingProgressionInfo(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  /**
   * Confirms that the pro has access to the books when the access type is unmanaged.
   * @param request
   * @returns
   */
  async confirmUnmanagedPlatformAccess(
    request: ConfirmUnmanagedPlatformAccessRequest,
  ) {
    return this.bookkeepingGrpcClient.confirmUnmanagedPlatformAccess(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  /**
   * Submits a bookkeeping assessment.
   * @param request
   * @returns
   */
  async submitBookkeepingAssessment(
    request: SubmitBookkeepingAssessmentRequest,
  ) {
    return this.bookkeepingGrpcClient.submitBookkeepingAssessment(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  /**
   * Starts a new bookkeeping cycle.
   * @param request
   * @returns
   */
  async startNewBookkeepingCycle(request: StartNewBookkeepingCycleRequest) {
    return this.bookkeepingGrpcClient.startNewBookkeepingCycle(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  /**
   * Completes a bookkeeping cycle.
   * @param request
   * @returns
   */
  async completeBookkeepingCycle(request: CompleteBookkeepingCycleRequest) {
    return this.bookkeepingGrpcClient.completeBookkeepingCycle(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  /**
   * Corrects a bookkeeping cycle.
   * @param request
   * @returns
   */
  async correctBookkeepingCycle(request: CorrectBookkeepingCycleRequest) {
    return this.bookkeepingGrpcClient.correctBookkeepingCycle(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }
}

/**
 * API client for Info Gathering.
 */
export class DocumentRequestAPI {
  private readonly documentRequestGrpcClient = createDocumentRequestGrpcClient(
    this.workDotnetConfig.baseURL,
  )

  private readonly grpcAuthenticator = createGrpcAuthenticator(
    this.workDotnetConfig,
  )

  constructor(private workDotnetConfig: APIConfig) {}

  /**
   * Submits a document request.
   * @param request
   * @returns
   */
  async submitDocumentRequest(request: SubmitDocumentRequestInput) {
    return this.documentRequestGrpcClient.submitDocumentRequest(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }
}

/**
 * Creates an instance of the Bookkeeping API.
 *
 * @param config
 */
export function createBookkeepingAPI(
  config: APIConfig,
  events$: Observable<RealtimeEvent>,
) {
  return new BookkeepingAPI(config, events$)
}

/**
 * Creates an instance of the JobTaxAssistant API.
 *
 * @param config
 */
export class JobTaxAssistantAPI {
  private readonly jobTaxAssistantGrpcClient = createJobTaxAssistantGrpcClient(
    this.workDotnetConfig.baseURL,
  )

  private readonly grpcAuthenticator = createGrpcAuthenticator(
    this.workDotnetConfig,
  )

  constructor(private workDotnetConfig: APIConfig) {}

  async get(request: GetJobTaxAssistantRequest) {
    return this.jobTaxAssistantGrpcClient.getJobTaxAssistant(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }
}

/**
 * Memoized feathers client factory.
 */
const createFeathersClient = memoize(async function createFeathersClient(
  baseUrl: string,
  token: string,
  params?: Record<string, unknown>,
) {
  const url = new URL(baseUrl)
  const pathname = url.pathname.endsWith('/')
    ? url.pathname.slice(0, -1)
    : url.pathname
  const socket = IO(url.origin, {
    path: `${pathname}/socket.io`,
    transports: ['websocket'],
    query: params,
  })
  await new Promise<void>((resolve, reject) => {
    socket.once('connect', () => {
      socket.emit('authenticate', { token }, (result: { success: boolean }) => {
        if (result.success) {
          return resolve()
        }
        console.error('Could not authenticate with event server: ', result)
        return reject(new Error('Could not authenticate with event server.'))
      })
    })
  })
  const app = (feathers() as any).configure(feathersSocket(socket))
  app.socket = socket
  return app
})

export interface FeathersEvent<T> {
  event: 'created' | 'updated' | 'patched' | 'removed'
  data: T
}

/**
 * Creates an event stream from a Feathers client.
 *
 * @param baseURL
 * @param token
 * @param params
 * @param service
 */
function fromFeathersEventStream<T>(
  baseURL: string,
  token: string,
  params: Record<string, unknown> | undefined,
  service: string,
): Observable<FeathersEvent<T>> {
  return defer(() =>
    from(createFeathersClient(baseURL, token, params)).pipe(
      switchMap(
        (client) =>
          new Observable<FeathersEvent<T>>((observer) => {
            const svc = client.service(service)
            const wire = (event: FeathersEvent<T>['event']) =>
              svc.on(event, (data: any) => observer.next({ event, data }))

            wire('created')
            wire('updated')
            wire('patched')
            wire('removed')

            return () => {
              client.socket.disconnect()
            }
          }),
      ),
    ),
  ).pipe(share())
}

/**
 * Creates a Work DotNET client that is memoized.
 */
const createJobGrpcClient = memoize(function createJobGrpcClient(
  baseUrl: string,
) {
  const client = new JobServicePromiseClient(baseUrl)
  enableDevTools(client)
  return client
})

/**
 * Creates a Work DotNET client that is memoized.
 */
const createJobEventGrpcClient = memoize(function createJobEventGrpcClient(
  baseUrl: string,
) {
  const client = new JobEventsServicePromiseClient(baseUrl)
  enableDevTools(client)
  return client
})

/**
 * Creates a Work DotNET client that is memoized.
 */
const createDraftGrpcClient = memoize(function createDraftGrpcClient(
  baseUrl: string,
) {
  const client = new DraftServicePromiseClient(baseUrl)
  enableDevTools(client)
  return client
})

/**
 * Creates a work dotnet client that is memoized.
 */
const createTicketGrpcClient = memoize(function createTicketGrpcClient(
  baseUrl: string,
) {
  const client = new TicketServicePromiseClient(baseUrl)
  enableDevTools(client)
  return client
})

/**
 * Creates an admin work dotnet client that is memoized.
 */
const createAdminJobGrpcClient = memoize(function createJobGrpcClient(
  baseUrl: string,
) {
  const client = new JobAdminServicePromiseClient(baseUrl)
  enableDevTools(client)
  return client
})

/**
 * Creates an admin work dotnet client that is memoized.
 */
const createDraftAdminGrpcClient = memoize(function createDraftAdminGrpcClient(
  baseUrl: string,
) {
  const client = new DraftAdminServicePromiseClient(baseUrl)
  enableDevTools(client)
  return client
})

/**
 * Creates a Work DotNET client that is memoized.
 */
const createInsightsGrpcClient = memoize(function createInsightsGrpcClient(
  baseUrl: string,
) {
  const client = new InsightsServicePromiseClient(baseUrl)
  enableDevTools(client)
  return client
})

/**
 * Creates a Work DotNET client that is memoized.
 */
const createInfoGatheringGrpcClient = memoize(
  function createInfoGatheringGrpcClient(baseUrl: string) {
    const client = new InfoGatheringServicePromiseClient(baseUrl)
    enableDevTools(client)
    return client
  },
)

const createConsultationGrpcClient = memoize(
  function createConsultationGrpcClient(baseUrl: string) {
    const client = new ConsultationServicePromiseClient(baseUrl)
    enableDevTools(client)
    return client
  },
)

const createTaxPreparationGrpcClient = memoize(
  function createTaxPreparationGrpcClient(baseUrl: string) {
    const client = new TaxPreparationServicePromiseClient(baseUrl)
    enableDevTools(client)
    return client
  },
)

const createBookkeepingGrpcClient = memoize(
  function createConsultationGrpcClient(baseUrl: string) {
    const client = new BookkeepingServicePromiseClient(baseUrl)
    enableDevTools(client)
    return client
  },
)

const createDocumentRequestGrpcClient = memoize(
  function createDocumentRequestGrpcClient(baseUrl: string) {
    const client = new DocumentRequestServicePromiseClient(baseUrl)
    enableDevTools(client)
    return client
  },
)

const createJobTaxAssistantGrpcClient = memoize(
  function createJobTaxAssistantGrpcClient(baseUrl: string) {
    const client = new JobTaxAssistantServicePromiseClient(baseUrl)
    enableDevTools(client)
    return client
  },
)
